import React, { ReactNode, useContext, useEffect, useRef, useState } from "react";

import { FeatureFlagContext, getActiveToken, isFeatureFlagsEnabled, useUserContext } from "utils";

import { DEFAULT_FALLBACK_AFTER_MS } from "pages/app/_app/components/feature-flag/FeatureFlagGate";

type FeatureFlagsReadyGateProps = {
  fallbackAfterMs?: number;
  children: ReactNode;
};

/**
 * Renders once FeatBit answers for the signed-in user, so routes and nav items behind flags never paint with the
 * anonymous answers (a reload of a flagged route would show "page not found" first). Once shown for a user it stays,
 * also while a farm switch identifies them again.
 */
export const FeatureFlagsReadyGate = ({ fallbackAfterMs = DEFAULT_FALLBACK_AFTER_MS, children }: FeatureFlagsReadyGateProps) => {
  const { getRawClient, ready, identifiedKeyId, identifying } = useContext(FeatureFlagContext);
  const { user } = useUserContext();
  const [waitedLongEnough, setWaitedLongEnough] = useState(false);
  const shownForUserId = useRef<string | null>(null);

  const token = getActiveToken();
  const awaitingIdentity = !!token && token !== "null" && (!user?.id || identifiedKeyId !== user.id);
  const clientNotReady = !getRawClient("client") || !ready.client;
  const resolving = !!user && isFeatureFlagsEnabled() && (clientNotReady || awaitingIdentity || identifying);

  useEffect(() => {
    if (!resolving) return undefined;

    setWaitedLongEnough(false);
    const timer = window.setTimeout(() => setWaitedLongEnough(true), fallbackAfterMs);
    return () => window.clearTimeout(timer);
  }, [resolving, fallbackAfterMs]);

  if (!resolving && user?.id) shownForUserId.current = user.id;
  if (resolving && !waitedLongEnough && shownForUserId.current !== user?.id) return null;

  return <>{children}</>;
};
